import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { makeStyles } from 'tss-react/mui';

import { irMenu } from '@lumirental/lumi-web-shared/lib/constants/IR/content';
import LANGUAGES from '@lumirental/lumi-web-shared/lib/constants/lang/LANGUAGES';
import { useLanguageContext } from '@lumirental/lumi-web-shared/lib/contexts/Language.context';
import getLocaleRoute from '@lumirental/lumi-web-shared/lib/utils/getLocaleRoute';
import changeLanguageTapped from '@lumirental/lumi-web-shared/lib/utils/gtm/changeLanguageTapped';
import navigateAndScrollToTop from '@lumirental/lumi-web-shared/lib/utils/lang/navigateAndScrollToTop';
import switchLanguagePath from '@lumirental/lumi-web-shared/lib/utils/lang/switchLanguagePath';

import { Button } from '@lumirental/lumi-web-components-ui';

import { manageSmoothScrolling } from '@/utils';
import { onTapEvent } from '@/utils/gtm';

import styles from './IRHeader.style';

const useStyles = makeStyles()(styles);

const LanguageSwitcher = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { isArabic } = useLanguageContext();

  const langBtnText = isArabic
    ? t('English_language_switch_button')
    : t('Arabic_language_switch_button');
  const dataLang = isArabic ? LANGUAGES.EN : LANGUAGES.AR;

  const handleLangChange = (evt) => {
    // send GTM event when language link tapped.
    changeLanguageTapped(false);

    const setLang = evt.currentTarget.dataset.lang;
    const newUrl = switchLanguagePath(setLang);

    // update the new url in browser
    global.location.href = newUrl;
  };

  return (
    <Typography
      component="a"
      data-lang={dataLang}
      className={`${classes.itemLink} ${classes.lang}`}
      onClick={handleLangChange}
    >
      {langBtnText}
    </Typography>
  );
};

const HeaderLogo = ({ language }) => {
  const { classes } = useStyles();

  return (
    <Grid item sm={1.7}>
      <div className={classes.logoWrapper}>
        <Link
          href={getLocaleRoute(language, '')}
          className={classes.logo}
          alt="Lumi Logo"
          title="Lumi Logo"
        >
          <Image
            src="/images/svg/lumi-logo.svg"
            alt="Lumi Logo"
            width={100}
            height={43}
            quality={100}
            priority
          />
        </Link>
      </div>
    </Grid>
  );
};
const HeaderItem = ({ item }) => {
  const router = useRouter();
  const { classes } = useStyles();

  const { language } = useLanguageContext();

  const { pathname } = router;
  const currentPage = pathname.substring(1);

  const handleMenuItemClick = (selectedMenu) => {
    const pathSplit = selectedMenu.split('/');
    const pageName = pathSplit?.length ? pathSplit?.at(-1) : 'IR';
    // send GA event
    onTapEvent(`IR_${pageName}`, `IR_${pageName}_tapped`);
  };

  const checkActiveMenu = (page) => {
    return `${classes.itemLink} ${currentPage === page ? 'active' : ''}`;
  };

  return (
    <Link passHref href={getLocaleRoute(language, item.url)} legacyBehavior>
      <Typography
        component="a"
        alt={item.title[language]}
        title={item.title[language]}
        className={checkActiveMenu(item.url)}
        aria-haspopup="true"
        onClick={() => handleMenuItemClick(item.url)}
      >
        {item.title[language]}
      </Typography>
    </Link>
  );
};

const SubMenu = ({ parent, page, subMenu }) => {
  const router = useRouter();

  const { language } = useLanguageContext();

  const [anchorEl, setAnchorEl] = useState(null);

  const { pathname } = router;
  const open = Boolean(anchorEl);

  const title = parent.title[language];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubMenuItemClick = (selectedMenu) => {
    const currentPage = pathname.substring(1);
    if (page !== currentPage) {
      navigateAndScrollToTop(router, page);
      // timeout to scroll after navigation
      setTimeout(() => {
        // manage Smooth Scrolling.
        const eleId = `#${selectedMenu}`;
        manageSmoothScrolling(eleId);
      }, 300);

      return;
    }
    // manage Smooth Scrolling.
    const eleId = `#${selectedMenu}`;
    manageSmoothScrolling(eleId);

    handleClose();
  };

  return (
    <div onMouseEnter={handleClick}>
      <HeaderItem item={parent} />
      <Button
        id={`basic-button-${title}`}
        variation="text"
        aria-controls={open ? `basic-menu-${title}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ padding: 0, minWidth: 'fit-content' }}
      >
        <ExpandMoreIcon />
      </Button>
      <Menu
        id={`basic-menu-${title}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {subMenu.map((el) => {
          return (
            <MenuItem key={el.id} onClick={() => handleSubMenuItemClick(el.id)}>
              {el.title[language]}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

const HeaderItems = ({ menuList }) => {
  const { classes } = useStyles();

  const { language } = useLanguageContext();

  return menuList.map((item) => {
    return (
      <ListItem
        key={`${item.title[language]}-menu-item`}
        className={classes.navigationItem}
      >
        {item?.subMenu?.length ? (
          <SubMenu page={item.url} subMenu={item.subMenu} parent={item} />
        ) : (
          <HeaderItem item={item} />
        )}
      </ListItem>
    );
  });
};

export default function IRHeader() {
  const { classes } = useStyles();

  const { language } = useLanguageContext();

  return (
    <>
      <div className={classes.header}>
        <Grid
          container
          className={classes.container}
          direction="row"
          justifyContent="space-between"
        >
          <HeaderLogo language={language} />
          <Grid item sm={10.3}>
            <List component="nav" className={classes.list}>
              <HeaderItems menuList={irMenu} />
              <LanguageSwitcher />
            </List>
          </Grid>
        </Grid>
      </div>
    </>
  );
}
